import React, { useState, useEffect, forwardRef } from 'react';
import { Message } from 'constants/messages';
import { Code   } from 'constants/codes';
import { Form } from 'helpers/FormHelpers';
import { RefObject } from 'react';
import { Toast } from 'helpers/ToastHelpers';
import { Utility } from 'helpers/UtilityHelpers';
import { OptionType, PrescriptionData, MedicationData, Privilege, MessageType } from 'models/BaseModel';
import Barcode from "react-barcode";
import DOMPurify from "dompurify";
import Select from 'react-select';
import { useNavigate } from "react-router-dom";
import { EventInput, Hooks } from 'helpers/CalendarImports';
import { Item } from 'models/ItemModel';

interface SelectAllProps { 
  onSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void; 
  selectAllChecked: boolean;
  extra?: number; 
}

/* This function generates a checkbox control that allows selecting all checkboxes in the item list
and also add N number of titles for the action links */
const SelectAll: React.FC<SelectAllProps> = ({ 
  onSelectAll, selectAllChecked, extra = 1 
}) => {
  return (
    <>
      <span className="list-col2">
        <input
          type="checkbox"
          className="select"
          checked={selectAllChecked}
          onChange={onSelectAll}
          title="Select all records on this page"
        />
      </span>
      {Array.from({ length: extra }, (_, index) => (
        <span key={index} className="list-col2 iaction"></span>
      ))}
    </>
  );
};

interface SearchBarProps { 
  onSearch: (value: string) => void; 
  onFilterToggle: () => void; 
  onGoClick: () => void; 
}

/* This function generates a search bar, filter, and Go button, adding their respective functionalities. */
const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  onFilterToggle,
  onGoClick,
}) => {
  const [inputValue, setInputValue] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onSearch(e.target.value); 
  };

  return (
    <div className="list-search-bar">
      <input
        type="text"
        className="list-search isearch clear"
        value={inputValue}
        onChange={handleChange}
        placeholder="Search..."
        title="Search Keywords"
      />

      <a
        className="ibutton bfilter filter fsetting"
        title="Filter"
        onClick={onFilterToggle}
      ></a>

      <button
        id="btnGo"
        type="button"
        className="tbutton bgo"
        title="Search"
        onClick={onGoClick}
      >Go</button>
    </div>
  );
};

interface DashProps { 
  data: [number, string, number][];
}

/* This function generate navigation button for pagination eg. next and back buttons and showing current page position */
const DashList: React.FC<DashProps> = ({ 
  data 
}) => {
  return (
    <div className='dash-list'>
      {data.map((values, index) => (
          <div key={index} className={`dash-item`} title={`${values[1]} : ${values[2]}`}>
            <div className={`dash-color${values[0]}`}>
              <span>{values[1]}</span>
              <p>{values[2]}</p>
            </div>
          </div>
        ))}
    </div>
  );
};

interface PaginationProps { 
  pageNo: number; 
  onChange: (page: number) => void; 
}

/* This function generate navigation button for pagination eg. next and back buttons and showing current page position */
const Pagination: React.FC<PaginationProps> = ({ 
  pageNo, onChange 
}) => {
  
  const handleBack = () => {
    if (pageNo > 1) {
        onChange(pageNo - 1);
    }
  };

  const handleNext = () => {
    onChange(pageNo + 1);
  };

  return (
    <div className="paging">
      <span>Page</span>
      <a className="back" onClick={handleBack}></a>
      <a className="pno">{pageNo}</a>
      <a className="next" onClick={handleNext}></a>
    </div>
  );
};

interface RecordInfoProps {
  pageNo: number;
  pageRows: number;
  loading: boolean;
  error: string | null;
  dataLength: number;
}

/* This function generate control to show number of record on this page and also total number of records available */
const RecordInfo: React.FC<RecordInfoProps> = ({
  pageNo,
  pageRows,
  loading,
  error,
  dataLength,
}) => {
  const verifyAllRecords = (): JSX.Element | null => {
    if (dataLength === pageRows) {
      return <p>1-{dataLength} of ...</p>;
    } else if (dataLength === 0) {
      return <p>{Message.Title.NOT_FOUND}</p>;
    } else if (dataLength > 0 && dataLength < pageRows) {
      if (pageNo > 1) {
        const last = (pageNo - 1) * pageRows;
        return <p>{`${last + 1}-${last + dataLength} of ${last + dataLength}`}</p>;
      } else {
        return <p>{`1-${dataLength} of ${dataLength}`}</p>;
      }
    } else if (error) {
      return <p>{error}</p>;
    } else if (loading) {
      return <p>Loading...</p>;
    }
    return null;
  };

  return <div className="record"><span>{verifyAllRecords()}</span></div>;
};

interface ReportRowsProps {
  dataLength: number;
}

/* This function generate control to show number of record on this page and also total number of records available */
const ReportRows: React.FC<ReportRowsProps> = ({
  dataLength,
}) => {
  const verifyAllRecords = (): JSX.Element | null => {
    return <p>{`${dataLength} record(s) found`}</p>;
  };

  return <div className="record"><span>{verifyAllRecords()}</span></div>;
};

interface DropdownProps {
  title: string;
  value?: number | string | null;
  options: { value: number; label: string }[];
  onChange: (value: number) => void;
  isSort?: boolean;
}

/* This function generates a select dropdown list, populates it with items from the options array,
and adds an onChange event to update the respective state variable using the setter function. */
const Dropdown: React.FC<DropdownProps> = ({
  title, 
  value,
  options, 
  onChange,
  isSort = false
}) => {
  const sortedOptions = isSort 
    ? [...options].sort((a, b) => a.label.localeCompare(b.label))
    : options;
  return (
    <div className="dropdown">
      <div className="ctitle">{title}</div>
      <div className="cdata">
        <select
          className={'floption'}
          value={value ? value : Code.Option.Common.ALL}
          onChange={(e) => onChange(Number(e.target.value))}
          title={title}
        >
          {sortedOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

interface DropdownInlineProps {
  title: string;
  options: { value: number; label: string }[];
  onChange: (value: number) => void;
  className?: string;
  isSort?: boolean;
  keyId?: string;
}

/* This function generates a select dropdown list, populates it with items from the options array,
and adds an onChange event to update the respective state variable using the setter function. */
const DropdownInline: React.FC<DropdownInlineProps> = ({
  title, 
  options, 
  onChange,
  className = '',
  isSort = false,
  keyId=''
}) => {
  const sortedOptions = isSort 
    ? [...options].sort((a, b) => a.label.localeCompare(b.label))
    : options;

  if (keyId === Code.Key.NEW) return null;
  return (
    <select
      className={`iloption ${className? className : ''}`}
      onChange={(e) => onChange(Number(e.target.value))}
      title={title}
    >
      {sortedOptions.map((item) => (
        <option key={item.value} value={item.value}>
          {item.label}
        </option>
      ))}
    </select>
  );
};

interface DropdownFilterProps {
  title: string;
  options: OptionType[];
  onChange: (value: number) => void;
}

const DropdownFilter: React.FC<DropdownFilterProps> = ({ title, options, onChange }) => {
  
  return (
    <div className="dropdown">
      <div className="ctitle">{title}</div>
      <div className="cdata">
        <Select
          options={options}
          onChange={(selectedOption) => {
            if (selectedOption) {
              onChange((selectedOption as OptionType).value);
            }
          }}
          styles={Code.Style.ListOption}
        />
      </div>
    </div>
  );
};

//values : value, width, align, visible
interface MultiGridProps {
  values: [(string | number | undefined)?, number?, typeof Code.Align[keyof typeof Code.Align]?, boolean?][];
  first?: number; 
  last?: number; 
  upper?: boolean;
}

/* This function generates N number of action button titles at the beginning and end of the row, 
adds N number of titles from the map array with width and alignment settings. */
const MultiGrid: React.FC<MultiGridProps> = ({ 
  values, 
  first = 0,
  last = 0, 
  upper = false
}) => {
  return (
    <>
      {Array.from({ length: first }, (_, idx) => (
        <span key={`extra-${idx}`} className="flcol list-col2 tcenter">
           ::
        </span>
      ))}
      {values.map(([value, width, align, visible], index) => {
          const alignClass = Utility.Get.alignClass(align);
          value = value === undefined ? '' : value; 
          width = width === undefined ? 5 : width;
          visible = visible === undefined ? true : visible;              
          return (
            <span key={index} className={`flcol copy list-col${width} ${alignClass} ${visible ? null : 'hide'}`}>
            {upper ? value?.toString().toUpperCase() : value}
          </span>
          );
      })}
      {Array.from({ length: last }, (_, idx) => (
        <span key={`extra-${idx}`} className="flcol list-col2 iaction"></span>
      ))}
    </>
  );
};

interface ListContextMenuProps {
  pos: { x: number; y: number };
  showMenu: boolean;
  formName: string;
  Privilege: Privilege;
  onCreate: () => void;
  onModify: () => void;
  onDelete: () => void;
  onClose: () => void;
  setShowMenu: (show: boolean) => void;
}

const ListContextMenu: React.FC<ListContextMenuProps> = ({ 
  pos, 
  showMenu, 
  formName, 
  Privilege,
  onCreate, 
  onModify, 
  onDelete,
  onClose,
  setShowMenu, 
}) => {
  const onReload = () => {
    window.location.reload();
  };  
  
  if (!showMenu) return null;

  return (
    <div className="context-menu" style={{ left: `${pos.x}px`, top: `${pos.y}px` }}>
      <ul>
        {
          Privilege.ALL || Privilege.OPEN || Privilege.MODIFY ? 
          <li>
            <a 
              className='icon-edit' 
              onClick={() => {
                setShowMenu(false);
                setTimeout(() => onModify(), 0);
              }}
              title={`Open this ${formName}`}
            >Open</a>
          </li> : null 
        } 
        {
          Privilege.ALL || Privilege.CREATE ? 
          <li>
            <a 
              className='icon-add' 
              onClick={() => {
                setShowMenu(false);
                setTimeout(() => onCreate(), 0);
              }}
              title={`Add new ${formName}`}
              >Create</a>
          </li> : null 
        }
        {
          Privilege.ALL || Privilege.DELETE ? 
          <li>
            <a 
              className='icon-delete' 
              onClick={() => {
                setShowMenu(false);
                setTimeout(() => onDelete(), 0);
              }}
              title={`Delete this ${formName}`}
              >Delete</a>
          </li> : null 
        }
        <li className='split'>
          <a 
            className='icon-reload' 
            onClick={onReload} 
            title={`Reload this list`}
            >Reload</a>
        </li>
        <li>
          <a 
            className='icon-close' 
            onClick={onClose} 
            title={`Close this list`}
            >Close</a>
        </li>
        {/* <li className='split'>
          <a className='icon-options'>Options</a>
        </li> */}
      </ul>
    </div>
  );
};

interface FormContextMenuProps {
  keyId: string;
  activeId: number | undefined;
  pos: { x: number; y: number };
  showMenu: boolean;
  formName: string;
  Privilege: Privilege;
  print?: boolean;
  handleDelete: () => void; 
  setShowMenu: (show: boolean) => void;
  children?: React.ReactNode;
}

const FormContextMenu: React.FC<FormContextMenuProps> = ({ 
  keyId,
  activeId, 
  pos, 
  showMenu, 
  formName, 
  Privilege,
  print = false,
  handleDelete,
  setShowMenu, 
  children = null
}) => {
  const navigate = useNavigate();
  const formRef = document.querySelector<HTMLFormElement>(`#${Code.Form.Name.EDIT}`);

  const onSaveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowMenu(false);
    if (formRef) {
      formRef.requestSubmit(); // Use requestSubmit() safely
    } else {
      console.error("Form not found!");
    }
  };

  const onReload = () => {
    window.location.reload();
  };  

  const onClose = () => {
    formName === Code.Form.Name.CLIENT 
    ? Form.Parent.TabClose()
    : Utility.Button.PopupClose()
  };

  const copySelectedText = () => {
    let textToCopy = '';
    const activeElement = document.activeElement;
    
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      const inputElement = activeElement as HTMLInputElement | HTMLTextAreaElement;
      const start = inputElement.selectionStart || 0;
      const end = inputElement.selectionEnd || 0;
      textToCopy = inputElement.value.substring(start, end);
    } else {
      textToCopy = window.getSelection()?.toString() || '';
    }
    
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => console.log('Copied:', textToCopy))
        .catch((err) => console.error('Copy failed:', err));
    }

    setShowMenu(false);
  };

  const cutSelectedText = () => {
    let textToCut = '';
    const activeElement = document.activeElement;
    
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      const inputElement = activeElement as HTMLInputElement | HTMLTextAreaElement;
      const start = inputElement.selectionStart || 0;
      const end = inputElement.selectionEnd || 0;
      textToCut = inputElement.value.substring(start, end);
      
      if (textToCut) {
        navigator.clipboard.writeText(textToCut)
          .then(() => {
            const currentValue = inputElement.value;
            inputElement.value = currentValue.substring(0, start) + currentValue.substring(end);
            inputElement.setSelectionRange(start, start);
            inputElement.focus();
            //console.log('Cut:', textToCut);
          })
          .catch((err) => console.error('Cut failed:', err));
      }
    } else {
      textToCut = window.getSelection()?.toString() || '';
      if (textToCut) {
        navigator.clipboard.writeText(textToCut)
          .then(() => {
            document.execCommand('delete');
            //console.log('Cut:', textToCut);
          })
          .catch((err) => console.error('Cut failed:', err));
      }
    }

    setShowMenu(false);
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      
      if (!text) {
        //console.log('Clipboard is empty');
        return;
      }
      
      const activeElement = document.activeElement;
      
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        const inputElement = activeElement as HTMLInputElement | HTMLTextAreaElement;
        
        const start = inputElement.selectionStart ?? 0;
        const end = inputElement.selectionEnd ?? 0;
        
        // Use setRangeText for better compatibility
        inputElement.setRangeText(text, start, end, 'end');
        inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        inputElement.focus();
        
        //console.log('Pasted:', text);
      } else {
        //alert('Please click on an input field first');
      }
    } catch (err) {
      console.error('Paste failed:', err);
    }
    setShowMenu(false);
  };

  if (!showMenu) return null;

  return (
    <div className="context-menu" style={{ left: `${pos.x}px`, top: `${pos.y}px` }}>
      <ul>
        <li>
          <a 
            accessKey='r'
            className='icon-reload' 
            onClick={onReload} 
            title={`Reload this ${formName} record`}
          >Reload</a>
        </li>

        {
          Privilege.ALL || Privilege.OPEN || Privilege.MODIFY ? 
          <li>
            <a 
              accessKey='s'
              className="icon-save" 
              onClick={onSaveClick} 
              title={`Save this ${formName} record`}
            >Save</a>
          </li> : null 
        } 
        
        {keyId !== Code.Key.NEW && print && (
          Privilege.ALL || Privilege.PRINT ? 
          <li>
            <a 
              accessKey='p'
              className="icon-print" 
              onClick={() => navigate(`/${formName}/print/${keyId}`)}
              title={`Print this ${formName} record`}
            >Print</a>
          </li> : null 
        )} 

        <li className='split'>
          <a 
            accessKey='x'
            className="icon-cut" 
            onClick={cutSelectedText} 
            title={`Cut selected Text`}
          >Cut</a>
        </li>

        <li>
          <a 
            accessKey='c'
            className="icon-copy" 
            onClick={copySelectedText} 
            title={`Copy selected Text`}
          >Copy</a>
        </li>

        <li>
          <a 
            accessKey='c'
            className="icon-paste" 
            onClick={pasteFromClipboard} 
            title={`Paste coppied Text`}
            onMouseDown={(e) => e.preventDefault()}
          >Paste</a>
        </li>

        <li className='split'>
          <a 
            accessKey='c'
            className="icon-close" 
            onClick={onClose} 
            title={`Close this ${formName} record`}
          >Close</a>
        </li>

        {children}

        <li className='split'>
        {
          activeId === 2 ? 
          <a 
            accessKey="l"
            className="icon-delete" 
            onClick={() => {
              setShowMenu(false);
              setTimeout(() => handleDelete(), 0);
            }}
            title={`Delete this ${formName} record (Alt+L)`} 
          >
          Delete 
          </a> 
          : 
          <a 
            accessKey="r"
            className="icon-restore" 
            onClick={() => {
              setShowMenu(false);
              setTimeout(() => handleDelete(), 0);
            }}
            title={`Restore this ${formName} record (Alt+L)`} 
          >
          Restore 
          </a> 
        }
        </li>

        {/* <li className='split'>
          <a className='icon-options'>Options</a>
        </li> */}
      </ul>
    </div>
  );
};

interface CalendarContextMenuProps {
  selectedEvent: EventInput | undefined;
  pos: { x: number; y: number };
  showMenu: boolean;
  formName: string;
  Privilege: Privilege;
  statusOptions?: OptionType[];
  postCheckOptions?: OptionType[];
  openLocalForm:  () => void;
  handleStatusChange: (statusId: number) => void; 
  handlePostCheckChange: (postCheckId: number) => void; 
  handleDelete: () => void; 
  setShowMenu: (show: boolean) => void;
}

const CalendarContextMenu: React.FC<CalendarContextMenuProps> = ({ 
  selectedEvent,
  pos, 
  showMenu, 
  formName, 
  Privilege,
  statusOptions,
  postCheckOptions,
  openLocalForm,
  handleStatusChange,
  handlePostCheckChange,
  handleDelete,
  setShowMenu, 
}) => {
  const navigate = useNavigate();
  const formRef = document.querySelector<HTMLFormElement>(`#${Code.Form.Name.EDIT}`);

  const onReload = () => {
    window.location.reload();
  };  

  const onClose = () => {
    formName === Code.Form.Name.CLIENT 
    ? Form.Parent.TabClose()
    : Utility.Button.PopupClose()
  };
  
  if (!showMenu) return null;

  return (
    <div className="context-menu" style={{ left: `${pos.x}px`, top: `${pos.y}px` }}>
      <ul>
        <li>
          <a 
            accessKey='r'
            className='icon-reload' 
            onClick={onReload} 
            title={`Reload this ${formName} record`}
          >Reload</a>
        </li>

        {selectedEvent && (
          Privilege.ALL || Privilege.OPEN || Privilege.MODIFY ? 
          <li>
            <a 
              accessKey='s'
              className="icon-edit" 
              onClick={() => {
                setShowMenu(false);
                openLocalForm();
              }}
              title={`Modify this ${formName} record`}
            >Modify</a>
          </li> : null 
        )} 
       
        <li>
          <a 
            accessKey='c'
            className="icon-close" 
            onClick={onClose} 
            title={`Close this ${formName} record`}
          >Close</a>
        </li>

        {statusOptions && (
          <li className="split more">
            <a className="icon-history" >Status</a>
            <ul>
              {statusOptions.map((stats, index) =>
                stats.value !== 0 ? (
                  <li key={index}>
                    <a
                      onClick={() => {
                        setShowMenu(false);
                        handleStatusChange(stats.value);
                      }} 
                      title={stats.label}
                    >
                      {stats.label}
                    </a>
                  </li>
                ) : null
              )}
            </ul>
          </li>
        )}

        {postCheckOptions && (
          <li className="more">
            <a className="icon-history" >Post Check</a>
            <ul>
              {postCheckOptions.map((stats, index) =>
                stats.value !== 2 ? (
                  <li key={index}>
                    <a
                      onClick={() => {
                        setShowMenu(false);
                        handlePostCheckChange(stats.value);
                      }} 
                      title={stats.label}
                    >
                      {stats.label}
                    </a>
                  </li>
                ) : null
              )}
            </ul>
          </li>
        )}

        {selectedEvent && (
          <li className='split'>
          {
            <a 
              accessKey="l"
              className="icon-delete" 
              onClick={() => {
                setShowMenu(false);
                setTimeout(() => handleDelete(), 0);
              }}
              title={`Delete this ${formName} record (Alt+L)`} 
            >
            Delete 
            </a> 
          }
          </li>)
        }

      </ul>
    </div>
  );
};

interface PrintContextMenuProps {
  keyId: string;
  pos: { x: number; y: number };
  formName: string;
  Privilege: Privilege;
  showMenu: boolean;
  setShowMenu: (show: boolean) => void;
}

const PrintContextMenu: React.FC<PrintContextMenuProps> = ({ 
  keyId,
  pos, 
  formName, 
  Privilege,
  showMenu, 
  setShowMenu, 
}) => {
  const navigate = useNavigate();
 
  const onReload = () => {
    window.location.reload();
  };  

  const onClose = () => {
    formName === Code.Form.Name.CLIENT 
    ? Form.Parent.TabClose()
    : Utility.Button.PopupClose()
  };
  
  if (!showMenu) return null;

  return (
    <div className="context-menu" style={{ left: `${pos.x}px`, top: `${pos.y}px` }}>
      <ul>
        {keyId !== Code.Key.NEW && (
          Privilege.ALL || Privilege.PRINT ? 
          <li>
            <a 
              accessKey='p'
              className="icon-print" 
              onClick={() => {
                setShowMenu(false);
                setTimeout(() => window.print(), 0);
              }}
              title={`Print this ${formName} record`}
            >Print</a>
          </li> : null 
        )} 

        {keyId !== Code.Key.NEW && (
          Privilege.ALL || Privilege.MODIFY ? 
          <li>
            <a 
              accessKey='p'
              className="icon-edit" 
              onClick={() => navigate(`/${formName}/${keyId}`)}
              title={`Modify this ${formName} record`}
            >Modify</a>
          </li> : null 
        )} 

        <li>
          <a 
            accessKey='r'
            className='icon-reload' 
            onClick={onReload} 
            title={`Reload this ${formName} record`}
          >Reload</a>
        </li>

        <li>
          <a 
            accessKey='c'
            className="icon-close" 
            onClick={onClose} 
            title={`Close this ${formName} record`}
          >Close</a>
        </li>

        {/* <li className='split'>
          <a className='icon-options'>Options</a>
        </li> */}
      </ul>
    </div>
  );
};

interface ReportContextMenuProps {
  pos: { x: number; y: number };
  showMenu: boolean;
  formName: string;
  setShowMenu: (show: boolean) => void;
  showFilter: boolean;
  setShowFilter: (show: boolean) => void;
}

const ReportContextMenu: React.FC<ReportContextMenuProps> = ({ 
  pos, 
  showMenu, 
  setShowMenu, 
  showFilter,
  setShowFilter, 
}) => {
  
  const onReload = () => {
    window.location.reload();
    setShowMenu(false);
  };  

  const onClose = () => {
    Form.Parent.TabClose();
    setShowMenu(false);
  };

  const onShowFilter = () => {
    setShowMenu(false);
    setShowFilter(!showFilter);
  };
  
  if (!showMenu) return null;

  return (
    <div className="context-menu" style={{ left: `${pos.x}px`, top: `${pos.y}px` }}>
      <ul>
        <li>
          <a 
            accessKey='r'
            className='icon-reload' 
            onClick={onReload} 
            title={`Reload report`}
          >Reload</a>
        </li>

        <li>
          <a 
            accessKey='r'
            className='icon-filter' 
            onClick={onShowFilter} 
            title={`Show Custom Filters`}
          >
            {showFilter ? 'Hide Filters' : 'Show Filters' }
          </a>
        </li>

        <li>
          <a 
            accessKey='c'
            className="icon-close" 
            onClick={onClose} 
            title={`Close report`}
          >Close</a>
        </li>

      </ul>
    </div>
  );
};

interface FormPopupProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  onRefresh: () => void;
}

/* This function generates an iframe popup to load a specific page, adds a close button, 
and upon closing, resets the iframe to a default page. After unloading the iframe, 
it sends a command to the parent form to reload the updated records. */
const FormPopup: React.FC<FormPopupProps> = ({ 
  iframeRef, 
  onRefresh 
}) => {
 
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === Code.Command.ON_POPUP_FORM_CLOSE) {
        closePopup();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const closePopup = () => {
    const popupElement = document.querySelector('.popup');
    if (popupElement) {
      popupElement.className = "popup";
      
      if (iframeRef.current) {
        iframeRef.current.src = '/pages/blank.html';
      }
    
      popupElement.addEventListener(
        'transitionend', 
        () => {
          onRefresh();
        }, 
        { once: true }
      );
    }
  };

  return (
    <div className="popup">
      <div className="pbox">
        <a className="pclose" onClick={closePopup} accessKey="q" title="Close form without saving (Alt+Q)" ></a>
        <iframe ref={iframeRef} className="pframe" src="/pages/blank.html"></iframe>
      </div>
    </div>
  );
};

const FormPopup2: React.FC<FormPopupProps> = ({ 
  iframeRef, 
  onRefresh 
}) => {
 
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === Code.Command.ON_POPUP_FORM_CLOSE) {
        closePopup();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const closePopup = () => {
    const popupElement = document.querySelector('.popup2');
    if (popupElement) {
      popupElement.className = "popup2";
      
      if (iframeRef.current) {
        iframeRef.current.src = '/pages/blank.html';
      }
    
      popupElement.addEventListener(
        'transitionend', 
        () => {
          onRefresh();
        }, 
        { once: true }
      );
    }
  };

  return (
    <div className="popup2">
      <div className="pbox">
        <a className="pclose" onClick={closePopup} accessKey="q" title="Close form without saving (Alt+Q)" ></a>
        <iframe ref={iframeRef} className="pframe" src="/pages/blank.html"></iframe>
      </div>
    </div>
  );
};

interface TabTitleProps {
  id: string;
  label: string;
  isActive: boolean;
  onClick: (id: string) => void;
}

const TabTitle: React.FC<TabTitleProps> = ({ id, label, isActive, onClick }) => {
  return (
    <a
      href="#"
      className={`tab-link ${isActive ? "active" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        onClick(id);
      }}
    >
      {label}
    </a>
  );
};


interface TabContentProps {
  id: string;
  isActive: boolean;
  children: React.ReactNode;
  title?: string
}

const TabContent: React.FC<TabContentProps> = ({ id, isActive, title, children }) => {
  return (
    <div
      id={id}
      className={`form-tab-content ${isActive ? "active" : ""}`}
      style={{ display: isActive ? "block" : "none" }}
    >
      {children}
    </div>
  );
};

interface ChildrenProps {
  children: React.ReactNode;
}

const ListInfo: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-info">
      {children}
    </div>
  );
};

const ReportInfo: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="report-info">
      {children}
    </div>
  );
};

const ListFrame: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-frame">
      {children}
    </div>
  );
};

const ListPanel: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-panel">
      {children}
    </div>
  );
};


const ReportPanel: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="report-panel">
      {children}
    </div>
  );
};

const ReportLeft: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="report-left">
      {children}
    </div>
  );
};

const ReportRight: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="report-right">
      {children}
    </div>
  );
};

const ReportTools: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="report-tool">
      {children}
    </div>
  );
};


const ListTools: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-tool">
      {children}
    </div>
  );
};

const DashTools: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="dash-tool">
      {children}
    </div>
  );
};

const ToolLeft: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-left">
      {children}
    </div>
  );
};

const ToolRight: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-right">
      {children}
    </div>
  );
};

const ListData: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-data">
      {children}
    </div>
  );
};

const DashData: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="dash-data">
      {children}
    </div>
  );
};

const ListBox: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-box">
      {children}
    </div>
  );
};

const ListTitle: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-title">
      {children}
    </div>
  );
};

const ListItem: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="list-item">
      {children}
    </div>
  );
};

const ListActions: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <span className="flcol action list-col2">
      <p className="link">
        {children}
      </p>
    </span>
  );
};

interface ListFilterProps {
  children: React.ReactNode;
  filterRef: React.RefObject<HTMLDivElement>;
}

const ListFilter: React.FC<ListFilterProps> = ({ filterRef,children }) => {
  return (
    <div className="list-filter" ref={filterRef}>
      {children}
    </div>
  );
};

const FormColumns: React.FC<MultiGridProps> = ({ 
  values, 
  first = 0,
  last = 0, 
  upper = false
}) => {
  return (
    <>
      {Array.from({ length: first }, (_, idx) => (
        <span key={`extra-${idx}`} className="flcol form-col2">
           ::
        </span>
      ))}
      {values.map(([value, width, align, hide], index) => {
          const alignClass = Utility.Get.alignClass(align);
          value = value === undefined ? '' : value; 
          return (
            <span key={index} className={`flcol form-col${width} ${alignClass} ${hide ? 'hide' : null }`}>
            {upper ? value?.toString().toUpperCase() : value}
          </span>
          );
      })}
      {Array.from({ length: last }, (_, idx) => (
        <span key={`extra-${idx}`} className="flcol form-col2 iaction"></span>
      ))}
    </>
  );
};


const ControlHeading: React.FC<MultiGridProps> = ({ 
  values, 
  first = 0,
  last = 0, 
  upper = false
}) => {
  return (
    <>
      {Array.from({ length: first }, (_, idx) => (
        <span key={`extra-${idx}`} className="flcol form-col2">
           ::
        </span>
      ))}
      {values.map(([value, width, align, hide], index) => {
          const alignClass = Utility.Get.alignClass(align);
          value = value === undefined ? '' : value; 
          return (
            <span key={index} className={`flcol form-col${width} ${alignClass} ${hide ? 'hide' : null }`}>
            {upper ? value?.toString().toUpperCase() : value}
          </span>
          );
      })}
      {Array.from({ length: last }, (_, idx) => (
        <span key={`extra-${idx}`} className="flcol form-col2 iaction"></span>
      ))}
    </>
  );
};


interface FormContextMenuWrapperProps {
  setMenuPosition: (position: { x: number; y: number }) => void;
  setShowMenu: (show: boolean) => void;
  children: React.ReactNode;
}

const FormFrame: React.FC<FormContextMenuWrapperProps> = ({ 
  setShowMenu, 
  setMenuPosition, 
  children 
}) => {

  const handleContextMenu = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setShowMenu(true);
  };

  return (
    <div className="form-frame" onContextMenu={handleContextMenu}>
      {children}
    </div>
  );
};

interface CalendarContextMenuWrapperProps {
  setMenuPosition: (position: { x: number; y: number }) => void;
  setShowMenu: (show: boolean) => void;
  children: React.ReactNode;
}

const CalendarFrame: React.FC<CalendarContextMenuWrapperProps> = ({ 
  setShowMenu, 
  setMenuPosition, 
  children 
}) => {

  const handleContextMenu = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) return;
    e.preventDefault();

    // 👇 Create a synthetic left-click
    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (target) {
      const leftClick = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      target.dispatchEvent(leftClick);
    }

    setMenuPosition({ x: e.clientX, y: e.clientY });
    setShowMenu(true);
  };

  return (
    <div className="fc-frame noselect" onContextMenu={handleContextMenu}>
      {children}
    </div>
  );
};

interface ReportContextMenuWrapperProps {
  setMenuPosition: (position: { x: number; y: number }) => void;
  setShowMenu: (show: boolean) => void;
  children: React.ReactNode;
}

const ReportFrame: React.FC<ReportContextMenuWrapperProps> = ({ 
  setShowMenu, 
  setMenuPosition, 
  children 
}) => {

  const handleContextMenu = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) return;
    e.preventDefault();

    //console.log("right clicked");
   
    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (target) {
      const leftClick = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      target.dispatchEvent(leftClick);
    }

    setMenuPosition({ x: e.clientX, y: e.clientY });
    setShowMenu(true);
  };

  return (
    <div className="report-frame" onContextMenu={handleContextMenu}>
      {children}
    </div>
  );
};


interface CalendarFormProps {
  children: React.ReactNode;
  setShowForm: (show: boolean) => void;
  formRef: React.RefObject<HTMLDivElement>;
  //setSelectedEvent: (selectedEvent: string) => void;
}

const CalendarForm: React.FC<CalendarFormProps> = ({ children, setShowForm, formRef  }) => {
  return (
    <div className="fc-form-frame">
      <div className="fc-form" ref={formRef}>
        <div className="fc-form-header">
          <span>Appointment</span>
          <a className="fc-form-close" onClick={() => setShowForm(false)}>×</a>
        </div>
        <div className="fc-form-body">
          {children}
        </div>
      </div>
    </div>
  );
};

interface FormSeparatorProps {
  Top?: number;
  Bottom?: number;
}

const FormSeparator: React.FC<FormSeparatorProps> = ({ Top, Bottom }) => {
  return (
    <div className={`line mt${Top} mb${Bottom}`}>&nbsp;</div>
  );
};


interface FormProps {
  className?: string;
  children: React.ReactNode;
}

const FormBox: React.FC<FormProps> = ({ className, children }) => {
  return (
    <div className={`box ${className || ''}`}>
      {children}
    </div>
  );
};

const FormRow: React.FC<FormProps> = ({ className, children }) => {
  return (
    <div className={`flbox ${className || ''}`}>
      {children}
    </div>
  );
};


const FormFlex: React.FC<FormProps> = ({ className,  children }) => {
  return (
    <div className={`flbox ${className || ''}`}>
      {children}
    </div>
  );
};


const FormResize: React.FC<FormProps> = ({ className,  children }) => {
  return (
    <div className={`flform ${className || ''}`}>
      {children}
    </div>
  );
};

const FormTable: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="form-table-scroll">
      <div className="form-table">
        {children}
      </div>
    </div>
  );
};

const PrintTable: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="print-table">
      {children}
    </div>
  );
};

const FormTitle: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="form-title">
      {children}
    </div>
  );
};

const FormFooter: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="form-footer">
      {children}
    </div>
  );
};

const PrintTitle: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="print-title">
      {children}
    </div>
  );
};

const FormList: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="form-list">
      {children}
    </div>
  );
};

const PrintList: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="print-list">
      {children}
    </div>
  );
};

const FormItem: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="form-item">
      {children}
    </div>
  );
};

const PrintItem: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="print-item">
      {children}
    </div>
  );
};

interface FormIndexProps {
  index: number | undefined;
  children: React.ReactNode;
}

const FormIndex: React.FC<FormIndexProps> = ({ index, children }) => {
  return (
    <div className="form-row " key={index}>
      {children}
    </div>
  );
};

interface BoxPanelProps {
  index: number | undefined;
  className?: string;
  children: React.ReactNode;
}

const BoxPanel: React.FC<BoxPanelProps> = ({ index, className, children }) => {
  return (
    <div className={`form-box-panel ${className}`} key={index}>
      {children}
    </div>
  );
};

const PrintIndex: React.FC<FormIndexProps> = ({ index, children }) => {
  return (
    <div className="print-row" key={index}>
      {children}
    </div>
  );
};

interface FormColumnProps {
  size?: [number,number,number,number?,string?] | null | undefined;
  align?: typeof Code.Align[keyof typeof Code.Align]; 
  children: React.ReactNode;
}

const FormColumn: React.FC<FormColumnProps> = ({ size, align = Code.Align.LEFT, children }) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
    <div className={`${alignClass} ${size ? Utility.Convert.pageColumnStyle(size) : "flcol page-col20"}`}>
      {children}
    </div>
  );
};

interface ButtonColumnProps {
  size?: [number,number,number,number,number,number?,string?] | null | undefined;
  align?: typeof Code.Align[keyof typeof Code.Align]; 
  children: React.ReactNode;
}

const ButtonColumn: React.FC<ButtonColumnProps> = ({ size, align = Code.Align.LEFT, children }) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
    <div className={`button-column ${alignClass} ${size ? Utility.Convert.fullColumnStyle(size) : " page-col20"}`}>
      {children}
    </div>
  );
};

interface ContainerProps {
  size?: [number,number,number,number?,string?] | null | undefined;
  className?: string | null | undefined;
  children: React.ReactNode;
}

const Container: React.FC<ContainerProps> = ({ size, className, children }) => {
  return (
    <div className={`container ${className ||''}`}>
      {children}
    </div>
  );
};

const FormData: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="form-data">
      {children}
    </div>
  );
};

const PrintData = forwardRef<HTMLDivElement, ChildrenProps>(({ children }, ref) => {
  return (
    <div ref={ref}>
      <div className="print-data">
        {children}
      </div>
    </div>
  );
});

const Printlandscape = forwardRef<HTMLDivElement, ChildrenProps>(({ children }, ref) => {
  return (
    <div ref={ref}>
      <div className="print-landscape">
        {children}
      </div>
    </div>
  );
});

const TabFrame: React.FC<ChildrenProps> = ({ children }) => {
  return (
    <div className="form-tab-frame">
      <div className="form-tab-title">
        {children}
      </div>
    </div>
  );
};

interface TabHeaderProps {
  titles: string[];
  activeTab: string;
  formName: string;
  info?: string;
  info2?: string;
  setActiveTab: (id: string) => void;
}

const TabHeader: React.FC<TabHeaderProps> = ({ 
  titles, 
  activeTab, 
  formName, 
  info ='',
  info2 ='',
  setActiveTab, 
}) => {
  return (
    <div className="form-tab-frame">
      <div className="form-tab-title">
        {titles.map((title, index) => {
          const id = `tab${index + 1}`;
          return (
            <a
              key={id}
              className={`tab-link ${activeTab === id ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(id);
                Utility.Storage.SetTab(formName.toLowerCase(), id);
              }}
            >
              {title.replace(" 0","")}
            </a>
          );
        })}
        {info ? <span className="form-tab-info" dangerouslySetInnerHTML={{ __html: `Current Balance : ${info} Coins` }} /> : ''}
        {info2 ? <span className="form-tab-info fright" dangerouslySetInnerHTML={{ __html: `Final Balance : ${info2} Coins` }} /> : ''}
      </div>
    </div>
  );
};

interface TabHeaderSuperProps {
  titles: [string, string, string][];
  activeTab: string;
  formName: string;
  info?: string;
  setActiveTab: (id: string) => void;
}

const TabHeaderSuper: React.FC<TabHeaderSuperProps> = ({ 
  titles, 
  activeTab, 
  formName, 
  info ='',
  setActiveTab, 
}) => {
  return (
    <div className="form-tab-frame">
      <div className="form-tab-title">
        {titles.map((title, index) => {
          const id = `tab${index + 1}`;
          return (
            <a
              key={id}
              className={`tab-link ${activeTab === id ? "active" : ""} ${title[2]}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(id);
                Utility.Storage.SetTab(formName.toLowerCase(), id);
              }}
            >
              {title[0]} {title[1] != "0" ? <span>{title[1]}</span> : ''}
            </a>
          );
        })}
        {info ? <span className="form-tab-info" dangerouslySetInnerHTML={{ __html: `Wallet Balance : ${info} Coins` }} /> : ''}
      </div>
    </div>
  );
};

type ActionLinkProps = {
  title: string,
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
};

export const ActionLink: React.FC<ActionLinkProps> = ({
  title,
  path,
  iframeRef,
}) => {
  return (
    <a onClick={() => Form.Show.Popup(iframeRef, path)}>
      {title}
    </a>
  );
};

type ActionEditProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
};

export const ActionEdit: React.FC<ActionEditProps> = ({
  path,
  iframeRef,
  Privilege,
}) => {
  return (
    Privilege.ALL || Privilege.MODIFY ?  ( 
      <a 
        className='icon-edit' 
        onClick={() => Form.Show.Popup(iframeRef, path)}
        >
          Edit
        </a>
    ) : null
  );
};

type ActionHistoryProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
};

export const ActionHistory: React.FC<ActionHistoryProps> = ({
  path,
  iframeRef,
  Privilege,
}) => {
  return (
    Privilege.ALL || Privilege.HISTORY ?  ( 
      <a 
        className='icon-history' 
        onClick={() => Form.Show.Popup(iframeRef, `${Code.Form.Name.HISTORY}/${path}`)}
        >
          History
        </a>
     ) : null
  );
};

interface ActionDeleteProps {
  activeId: number;
  keyId: string;
  Privilege: Privilege;
  setSelectedItems: (selectedKeyId: string[]) => void;
  setMessage: (message: MessageType) => void;
}

const ActionDelete: React.FC<ActionDeleteProps> = ({
  activeId,
  keyId,
  Privilege,
  setSelectedItems,
  setMessage,
}) => {
  return  Privilege.ALL || Privilege.DELETE ?  (
     <a 
      className='icon-delete' 
      onClick={()=>Toast.Delete.SingleRecord(activeId, keyId, setSelectedItems, setMessage)}
      >
        Delete
      </a> 
   ) : null
};

interface ActionAllProps {
  formName: string;
  activeId: number;
  keyId: string | undefined;
  Privilege: Privilege;
  iframeRef: RefObject<HTMLIFrameElement>;
  setSelectedItems: (selectedKeyId: string[]) => void;
  setConfirm: (message: MessageType) => void;
  children: React.ReactNode;
}

/* This reusable control dynamically generates popup action's links: Edit, History, and Delete 
in the item list, enabling their respective functionalities. */
const ActionAll: React.FC<ActionAllProps> = ({
  formName,
  activeId,
  keyId,
  iframeRef,
  Privilege,
  setSelectedItems,
  setConfirm,
  children,
}) => {
  return  (
    <>
      { 
        Privilege.ALL || Privilege.MODIFY ? 
        <a 
          className='icon-edit' 
          onClick={() => Form.Show.Popup(iframeRef, Utility.Join.Url(formName,keyId))}>
            Edit
        </a> : null
      } 
      {children}
      {/* {
        Privilege.ALL || Privilege.HISTORY ? 
        <a 
          className='icon-history' 
          onClick={() => Form.Show.Popup(iframeRef, Utility.Join.Url(Code.Form.Name.HISTORY,formName,keyId))}>
            History
        </a> : null 
      } */}
      {
        activeId == Code.Option.Active.ACTIVE && (Privilege.ALL || Privilege.DELETE) ? 
        <a 
          className='icon-delete' 
          onClick={() => Toast.Delete.SingleRecord(activeId, keyId, setSelectedItems, setConfirm)}>
            Delete
        </a> : null 
      }
    </>
  );
};

type CheckboxControlProps = {
  keyId: string | undefined;
  selectedItems: string[];
  toggleSelection: (key: string, setSelectedItems: (selectedKeyId: string[]) => void) => void;
  setSelectedItems: (selectedKeyId: string[]) => void;
};

/* This reusable control dynamically generates a checkbox in the item list, adding the selected
key ID to the selected items and enabling the delete button when checked. */
const CheckboxControl: React.FC<CheckboxControlProps> = ({
  keyId,
  selectedItems,
  toggleSelection,
  setSelectedItems,
}) => {
  return (
    <span className="flcol list-col2">
      <input
        type="checkbox"
        className="select"
        data-key={keyId}
        checked={selectedItems.includes(keyId!)}
        onChange={() => toggleSelection(keyId!, setSelectedItems)}
        title="Select this record"
      />
    </span>
  );
};

type ItemModifyProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
  icon?: string;
  title?: string;
};

const ItemModify: React.FC<ItemModifyProps> = ({
  path,
  iframeRef,
  Privilege,
  icon = '',
  title = ''
}) => {
  ////console.log("Rendering ItemModify with path:", path);
  return (
    Privilege.ALL || Privilege.OPEN ?
    <a 
      className={`flcol list-col2 ${icon ? icon : 'edit'}`} 
      onClick={()=>Form.Show.Popup(iframeRef, path)} 
      title={title ? title : "Open this record"}
    >
      &nbsp;
    </a>
    :
    <a 
      className="flcol list-col2 noedit" 
      title="No privilege to open this record"
    >
      &nbsp;
    </a>
  );
};

type ItemDoubleProps = {
  path: string;
  path2: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  iframeRef2: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
  icon?: string;
  title?: string;
};

const ItemDouble: React.FC<ItemDoubleProps> = ({
  path,
  path2,
  iframeRef,
  iframeRef2,
  Privilege,
  icon = '',
  title = ''
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault(); // Prevent default anchor behavior
    
    if (e.ctrlKey || e.metaKey) {
      Form.Show.Popup2(false,iframeRef, iframeRef2, path2)
    } else {
      Form.Show.Popup2(true,iframeRef, iframeRef2, path)
    }
  };

  return (
    Privilege.ALL || Privilege.OPEN ?
    <a 
      className={`flcol list-col2 ${icon ? icon : 'edit'}`} 
      onClick={handleClick} 
      title={title ? title : "Open this record"}
    >
      &nbsp;
    </a>
    :
    <a 
      className="flcol list-col2 noedit" 
      title="No privilege to open this record"
    >
      &nbsp;
    </a>
  );
};

type ItemSaleProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
  icon?: string;
  title?: string;
};

const ItemSale: React.FC<ItemSaleProps> = ({
  path,
  iframeRef,
  Privilege,
  icon = '',
  title = ''
}) => {

  const saleId = path.split('/').pop()?.toLowerCase() ?? '';
  const hasSale = saleId !== "2" && saleId !== "new";

  const hasPrivilege = Privilege.ALL || Privilege.OPEN;

  if (!hasPrivilege) {
    return (
      <a
        className="flcol list-col2 nosale"
        title="No privilege to open this record"
      >
        &nbsp;
      </a>
    );
  }

  // user has privilege
  if (hasSale) {
    return (
      <a
        className={`flcol list-col2 ${icon || 'sale'}`}
        onClick={() => Form.Show.Popup(iframeRef, path)}
        title={title || "Open this record"}
      >
        &nbsp;
      </a>
    );
  }

  // user has privilege but no saleID match
  return (
    <a
      className="flcol list-col2 none"
      title="This has no sale linked to it"
    >
      &nbsp;
    </a>
  );
};


type ItemPrintProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
};

const ItemPrint: React.FC<ItemPrintProps> = ({
  path,
  iframeRef,
  Privilege
}) => {
  return (
    Privilege.ALL || Privilege.PRINT ?
    <a className="flcol list-col2 print" onClick={()=>Form.Show.Popup(iframeRef, path)} title="Print this record">&nbsp;</a>
    :
    <a className="flcol list-col2 noprint" title="No privilege to print this record">&nbsp;</a>
  );
};

type ItemPreviewProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
};

const ItemPreview: React.FC<ItemPreviewProps> = ({
  path,
  iframeRef,
  Privilege
}) => {
  return (
    Privilege.ALL || Privilege.OPEN ?
    <a className="flcol list-col2 preview" onClick={()=>Form.Show.Popup(iframeRef, path)} title="Preview this record">&nbsp;</a>
    :
    <a className="flcol list-col2 nopreview" title="No privilege to preview this record">&nbsp;</a>
  );
};

type HashLinkProps = {
  hashId: number | undefined;
};

const HashLink: React.FC<HashLinkProps> = ({
  hashId,
}) => {
  return (
    <a 
      className={`flcol list-col2 down`} 
      href={`#hash${hashId}`}
      title={"Move to details"}
    >
      &nbsp;
    </a>
  );
};

type HashTargetProps = {
  hashId: number | undefined;
};

const HashTarget: React.FC<HashTargetProps> = ({
  hashId,
}) => {
  return (
    <div id={`hash${hashId}`}>&nbsp;</div> 
  );
};

type ItemLinkProps = {
  path: string;
  value: string | number | undefined;
  width: number;
  iframeRef: RefObject<HTMLIFrameElement>;
  title?: string
};

const ItemLink: React.FC<ItemLinkProps> = ({
  path,
  value,
  width,
  iframeRef,
  title = ''
}) => {
  ////console.log("Rendering ItemLink with path:", path);
  return (
    <span 
      className={`flcol cp list-col${width}`} 
      onClick={()=>Form.Show.Popup(iframeRef, path)} 
      {...(title ? { 'title': `Open ${title}` } : {})}
    >
      {value}
    </span>
  );
};

type FormStatus = { id: number; status: number };

type SaleLinkProps = {
  data?: string; // multiple JSON objects separated by ;
  width?: number;
  iframeRef: React.RefObject<HTMLIFrameElement>;
};

const SaleLink: React.FC<SaleLinkProps> = ({ data, width = 5, iframeRef }) => {
  if (!data || data === "{}") 
    return <span className={`flcol cp list-col${width}`}>&nbsp;</span>;

  // Split data into individual JSON objects
  const parts = data
    .split(";")
    .map(p => p.trim())
    .filter(Boolean);

  // Parse each part safely
  const objects: Record<string, FormStatus>[] = parts.map(part => {
    try {
      return JSON.parse(part) as Record<string, FormStatus>;
    } catch {
      return {}; // skip invalid JSON
    }
  });

  return (
    <span className={`flcol cp list-col${width}`}>
      {objects.map((obj, index) => (
        <div className="sale-links" key={index}>
          {Object.entries(obj).map(([key, { id, status }]) => {
            const cleanKey = key.replace(/\d+$/, "");
            const label = cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1);

            if (status === 2) {
              return (
                <p
                  key={key}
                  className={`sale-pending sale-icon-${label.toLowerCase()}`}
                  title={`${label} Pending`}
                >
                  &nbsp;
                </p>
              );
            } else {
              return (
                <p
                  key={key}
                  className={`sale-done sale-icon-${label.toLowerCase()}`}
                  onClick={() => {
                    if (iframeRef?.current) {
                      Form.Show.Popup(
                        iframeRef,
                        Utility.Join.Url(
                          label.toLowerCase().replace("order", "orders"),
                          id.toString()
                        )
                      );
                    }
                  }}
                  title={`Open ${label}`}
                >
                  &nbsp;
                </p>
              );
            }
          })}
        </div>
      ))}
    </span>
  );
};

// type SaleLinkProps = {
//   data?: string;
//   width?: number;
//   iframeRef: React.RefObject<HTMLIFrameElement>;
// };

// const SaleLink: React.FC<SaleLinkProps> = ({ data, width = 5, iframeRef }) => {
//   if (!data) return null; // empty data → nothing

//   const parts = data
//     .split(";")
//     .map(p => p.trim())
//     .filter(Boolean);

//   const objects = parts.map(p => JSON.parse(p)) as Record<string, number>[];

//   return (
//     <span className={`flcol cp list-col${width}`} >
//       {objects.map((obj, index) => (
//         <div className='sale-links' key={index}>
//           {Object.entries(obj).map(([key, value]) => {
//             const cleanKey = key.replace(/\d+$/, ""); // remove trailing numbers
//             const label = cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1);
//             if(value === 2) 
//             {
//               return (
//                 <p 
//                   key={key}  
//                   className={`sale-pending sale-icon-${label.toLowerCase()}`} 
//                   title={`${label} Pending`}
//                 >
//                   &nbsp;
//                 </p>
//               );
//             }
//             else{
//               return (
//                 <p 
//                   key={key}
//                   className={`sale-done sale-icon-${label.toLowerCase()}`}
//                   onClick={() => Form.Show.Popup(iframeRef, Utility.Join.Url(label.toLowerCase().replace("order","orders"), value.toString()))}
//                   title={`Open ${label}`}
//                 >
//                   &nbsp;
//                 </p>
//               );
//             }
            
//           })}
//         </div>
//       ))}
//     </span>
//   );
// };

type ItemTextProps = {
  value: string | number | undefined;
  width?: number;
  align?: typeof Code.Align[keyof typeof Code.Align]
};

const ItemText: React.FC<ItemTextProps> = ({
  value,
  width = 5,
  align = Code.Align.LEFT,
}) => {
 const alignClass = Utility.Get.alignClass(align);
  value = value === undefined ? '' : value;
  return (
    <span className={`flcol ${alignClass} copy list-col${width}`}>{value}</span>
  );
};

type ItemDateProps = {
 value?: Date | undefined;
 width?: number;
};

const ItemDate: React.FC<ItemDateProps> = ({
 value,
 width = 5,
}) => {
 let dateOnly ='', dateWithTime = '';
 if(value !== undefined) {
   dateOnly = Utility.Format.DateOnly(value);
   dateWithTime = Utility.Format.DateTime(value);
 } 
 
 return (
   <span className={`flcol list-col${width}`} title={dateWithTime}>{dateOnly}</span>
 );
};

type ItemNumberProps = {
 value: string | number | undefined;
 width?: number;
};

const ItemNumber: React.FC<ItemNumberProps> = ({
 value,
 width = 5,
}) => {
 value = value === undefined ? '' : value;
 return (
   <span className={`flcol tright list-col${width}`}>{value}</span>
 );
};

interface ContextMenuWrapperProps<T> {
  itemKey: string | undefined;
  data: T;
  setMenuPosition: (position: { x: number; y: number }) => void;
  setSelectedData: React.Dispatch<React.SetStateAction<T | null>>;
  setShowMenu: (show: boolean) => void;
  children: React.ReactNode;
}

export function ContextMenuWrapper<T>({
  itemKey,
  data,
  setMenuPosition,
  setSelectedData,
  setShowMenu,
  children,
}: ContextMenuWrapperProps<T>) {
  const handleContextMenu = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) return;
    e.preventDefault(); 
    Form.Show.ContextMenu<T>({
      event: e,
      data,
      setMenuPosition,
      setSelectedData,
      setShowMenu,
    });
  };

  return (
    <div key={itemKey} onContextMenu={handleContextMenu}>
      {children}
    </div>
  );
}

type FormCheckProps = {
  index: number;
  checked: boolean | undefined;
  handleItemChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
};

/* This reusable control dynamically generates a checkbox in the item list, 
   adding the selected key ID to the selected items and enabling the delete button when checked. */
const FormCheck: React.FC<FormCheckProps> = ({ index, checked, handleItemChange }) => {
  return (
    <div className="form-col2 flcheck">
      <span>{index + 1}</span>
      <input
        type="checkbox"
        name="selected"
        checked={checked ?? false}
        className="select"
        title="Delete selected entry on save"
        onChange={(e) => handleItemChange(index, e)}
      />
    </div>
  );
};

type FormEditProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
};

const FormEdit: React.FC<FormEditProps> = ({
  path,
  iframeRef,
  Privilege,
}) => {
  return (
    Privilege.ALL || Privilege.OPEN ?
    <div className="fl form-col2 edit" onClick={()=>Form.Show.Popup(iframeRef, path)}>&nbsp;</div>
    :
    <div className="fl form-col2 noedit" title="No privileges to open this record">&nbsp;</div>
  );
};

type FormSaleProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
};

const FormSale: React.FC<FormSaleProps> = ({
  path,
  iframeRef,
  Privilege,
}) => {
  const saleId = path.split('/').pop()?.toLowerCase() ?? '';
  const hasSale = saleId !== "2" && saleId !== "new";

  const hasPrivilege = Privilege.ALL || Privilege.OPEN;

  if (!hasPrivilege) {
    return (
      <a
        className="fl form-col2 nosale"
        title="No privilege to open this record"
      >
        &nbsp;
      </a>
    );
  }

  // user has privilege
  if (hasSale) {
    return (
      <a
        className={`fl form-col2 sale`}
        onClick={() => Form.Show.Popup(iframeRef, path)}
        title={"Open this record"}
      >
        &nbsp;
      </a>
    );
  }

  // user has privilege but no saleID match
  return (
    <a
      className="fl form-col2 none"
      title="This has no sale linked to it"
    >
      &nbsp;
    </a>
  );
};

type FormPrintProps = {
  path: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  Privilege: Privilege;
};

const FormPrint: React.FC<FormPrintProps> = ({
  path,
  iframeRef,
  Privilege,
}) => {
  return (
    Privilege.ALL || Privilege.OPEN ?
    <div className="fl form-col2 print" onClick={()=>Form.Show.Popup(iframeRef, path)}>&nbsp;</div>
    :
    <div className="fl form-col2 noprint" title="No privileges to open this record">&nbsp;</div>
  );
};

type FormTextProps = {
  id: string,
  value: string | number | undefined;
  visible?: boolean,
  disabled?: boolean
  size?: [number,string?] | null | undefined;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

/* This reusable control dynamically generates a checkbox in the item list, 
   adding the selected key ID to the selected items and enabling the delete button when checked. */
const FormText: React.FC<FormTextProps> = ({ id, value, size, visible = true, disabled = false, onChange }) => {
  return (
    <>
      <div className={`flcol fltext ${size ? Utility.Convert.formListStyle(size) : null} ${visible ? null :'hide'}`}>
        <input 
          type="text" 
          id={id} 
          value={value || ''} 
          title={value ? value.toString() : ''}
          onChange={onChange}
          {...(disabled && { readOnly: true })}
          
        />
      </div>  
    </>
  );
};

type PrintTextProps = {
  value: string | number | undefined;
  visible?: boolean,
  size?: [number,string?] | null | undefined;
  align?: typeof Code.Align[keyof typeof Code.Align]
};

const PrintText: React.FC<PrintTextProps> = ({ value, size, visible = true, align  }) => {
 const alignClass = Utility.Get.alignClass(align);
  return (
    <>
      <div className={`flcol print-text ${size ? Utility.Convert.formListStyle(size) : null} ${alignClass} ${visible ? null :'hide'}`}>
        <div><span>{value || ''}</span></div>
      </div>  
    </>
  );
};

type PrintNumberProps = {
  value: number | undefined;
  visible?: boolean,
  size?: [number,string?] | null | undefined;
  align?: typeof Code.Align[keyof typeof Code.Align]
};

const PrintNumber: React.FC<PrintNumberProps> = ({ value, size, visible = true, align  }) => {
  const alignClass = Utility.Get.alignClass(align);
  let formattedValue = '';

  if (value !== undefined && value !== null) {
    formattedValue = value < 1000 ? value.toFixed(2) : value.toString();
  }
  return (
    <>
      <div className={`flcol print-text tright ${size ? Utility.Convert.formListStyle(size) : null} ${alignClass} ${visible ? null :'hide'}`}>
        <div><span>{formattedValue}</span></div>
      </div>  
    </>
  );
};

type PrintProductProps = {
  product: string | undefined;
  sku: string | undefined;
  comment: string | undefined;
  size?: [number,string?] | null | undefined;
};

const PrintProduct: React.FC<PrintProductProps> = ({ product, sku, comment, size  }) => {
  let value = '';
  if(product) value += `<span class='bold'>${product}</span>`;
  if(sku) value += `<span>SKU: ${sku}</span>`;
  if(comment) value += `<span>${comment}</span>`;
 
  return (
    <>
      <div className={`flcol print-text ${size ? Utility.Convert.formListStyle(size) : null}`}>
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || "") }} ></div>
      </div>  
    </>
  );
};

type PrintTermProps = {
  value?: string;
  title?: string;
};

const PrintTerm: React.FC<PrintTermProps> = ({ value, title  }) => {
  return (
    <div className="print-terms">
      {/* <div><strong>{title ? title : 'Thank you for visiting us!'} </strong></div> */}
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || "") }} />
    </div>
  );
};

type FormNumberProps = {
  id: string,
  value: number | undefined;
  visible?: boolean,
  disabled?: boolean
  size?: [number,string?] | null | undefined;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const FormNumber: React.FC<FormNumberProps> = ({ id, value, size, visible = true, disabled = false, placeholder = '', onChange }) => {
  return (
    <>
      <div className={`fltext ${size ? Utility.Convert.formListStyle(size) : null} ${visible ? null:'hide'}`}>
        <input 
          type="number" 
          id={id} 
          name={id}
          placeholder={placeholder}
          value={value} 
          onChange={onChange}
          {...(disabled && { disabled: true })}
          className='tright'
        />
      </div>  
    </>
  );
};

type FormAmountProps = {
  name: string,
  value: number | undefined;
  visible?: boolean,
  disabled?: boolean
  size?: [number,string?] | null | undefined;
  index?: number;
  onItemChange?: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
};

const FormAmount: React.FC<FormAmountProps> = ({ name, value, size, visible = true, disabled = false, index, onItemChange }) => {
  return (
    <>
      <div className={`fltext ${size ? Utility.Convert.formListStyle(size) : null} ${visible ? null : 'hide' }`}>
        <input 
          type="text" 
          name={name} 
          value={`$${value || ''}`} 
          {...(disabled && { disabled: true })}
          {...(onItemChange !== undefined && index !== undefined ? { onChange: (e) => onItemChange(index, e) } : {})}
          className='tright'
        />
      </div>  
    </>
  );
};

type FormDateProps = {
  name: string,
  value: Date | undefined;
  visible?: boolean,
  disabled?: boolean
  size?: [number,string?] | null | undefined;
  index?: number;
  handleItemChange?: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
};

/* This reusable control dynamically generates a checkbox in the item list, 
   adding the selected key ID to the selected items and enabling the delete button when checked. */
const FormDate: React.FC<FormDateProps> = ({ name, value, size, visible = true, disabled = false, index, handleItemChange }) => {
  return (
    <>
      <div className={`fltext ${size ? Utility.Convert.formListStyle(size) : null} ${ visible ? null : 'hide' }`}>
        <input 
          type="text" 
          name={name} 
          value={ value ? Utility.Format.DateOnly(new Date(value)) : '' } 
          {...(disabled && { disabled: true })}
          {...(handleItemChange && index && { onChange: (e) => handleItemChange(index, e) })}
        />
      </div>  
    </>
  );
};

type PrintDateProps = {
  value: Date | undefined;
  visible?: boolean,
  size?: [number,string?] | null | undefined;
  align?: typeof Code.Align[keyof typeof Code.Align]
};

const PrintDate: React.FC<PrintDateProps> = ({ value, size, visible = true, align  }) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
    <>
      <div className={`flcol print-text ${size ? Utility.Convert.formListStyle(size) : null} ${alignClass} ${visible ? null :'hide'}`}>
        <div><span>{Utility.Format.DateOnly(value) || ''}</span></div>
      </div>  
    </>
  );
};


type FormOptionProps = {
  id: string,
  value: string | number | undefined;
  optionValues: OptionType[], 
  visible?: boolean,
  disabled?: boolean
  size?: [number,string?] | null | undefined;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

/* This reusable control dynamically generates a checkbox in the item list, 
   adding the selected key ID to the selected items and enabling the delete button when checked. */
const FormOption: React.FC<FormOptionProps> = ({ id, value, optionValues, size, visible = true, disabled = false, onChange }) => {
  return (
    <>
      <div className={`floption ${size ? Utility.Convert.formListStyle(size) : null} ${visible ? null:'hide'}`}>
        <select 
          id={id}
          name={id}
          value={String(value ?? '')}
          onChange={onChange}
          >
          {optionValues.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
        </select>
      </div>  
    </>
  );
};

type FormTextAreaProps = {
  index: number;
  value: string | undefined;
  handleItemChange: (index: number, e: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

/* This reusable control dynamically generates a checkbox in the item list, 
   adding the selected key ID to the selected items and enabling the delete button when checked. */
const FormTextArea: React.FC<FormTextAreaProps> = ({ index, value, handleItemChange }) => {
  return (
    <>
      <div className="fl form-col2 flcheck"></div>
      <div className='fl form-col98 flarea'>
        <textarea 
          name="item_comment" 
          value={value || ''} 
          onChange={(e) => handleItemChange(index, e)} 
        />
      </div>
    </>
  );
};

type OrderTextAreaProps = {
  index: number;
  path?: string;
  iframeRef: RefObject<HTMLIFrameElement>;
  icon?: string;
  title?: string;
  specification?: number;
  data?: string;
  productName?: string;
  utncode?: string;
  skucode?: string;
  frameStatus?: string;
  orderStatus?: string;
  price?: number;
  quantity?: number;
  discAmount?: number;
  discPercentage?: number;
  tax1Amount?: number;
  tax1Percentage?: number;
  netAmount?: number;
};

/* This reusable control dynamically generates a checkbox in the item list, 
   adding the selected key ID to the selected items and enabling the delete button when checked. */
const OrderTextArea: React.FC<OrderTextAreaProps> = ({ 
  index, 
  path, 
  iframeRef,
  icon ='',
  title ='', 
  specification = 2,
  data ='', 
  productName ='',
  utncode ='',
  skucode ='',
  frameStatus ='',
  orderStatus ='',
  price = 0,
  quantity=0,
  discAmount = 0,
  discPercentage = 0,
  tax1Amount = 0,
  tax1Percentage = 0,
  netAmount = 0,
}) => {
  const utnSku = skucode ? ` - SKU: <span class='copy'>${skucode}</span>`: utncode ? ` - UTN: <span class='copy'>${utncode}</span>`:'';
  const productInfo = `${productName ? `<div class='format-product'>${productName} ${specification == Number(Code.Option.Control.FRAME) ?  utnSku : ''}</div>`: ''} ${data}`;
  const statusInfo = `${frameStatus}<br/>${orderStatus}`;
  const priceInfo = `<span class="fwb">$${price}</span><br/>${quantity}`;
  const discountInfo = `<span class="fwb">$${discAmount}</span><br/>${discPercentage}%`;
  const taxInfo = `<span class="fwb">$${tax1Amount}</span><br/>${tax1Percentage}%`;
  const AmountInfo = `<span class="fwb">$${netAmount}</span>`;
  let style1 = 'fl form-col46 flarea pa2 bright';
  const style2 = 'fl form-col10 flarea pa2 bright tright format-status';
  const style3 = 'fl form-col10 flarea pa2 tright format-status';

  if(!path){
    style1 = 'fl form-col50 flarea pa2 bright';
  }

  return (
    <>
      <div className="form-item">
        {path &&
          <div className="fl form-col2 flcheck">
            <a 
              className={`form-icon ${icon ? icon : 'edit'}`} 
              onClick={()=>Form.Show.Popup(iframeRef, path)} 
              title={title ? title : "Open this record"}
            >
              &nbsp;
            </a>
          </div>
        }
        
        <div className={style1} dangerouslySetInnerHTML={{ __html: productInfo }} ></div>
        <div className={style2} dangerouslySetInnerHTML={{ __html: statusInfo }}></div>
        <div className={style2} dangerouslySetInnerHTML={{ __html: priceInfo }}></div>
        <div className={style2} dangerouslySetInnerHTML={{ __html: discountInfo }}></div>
        <div className={style2} dangerouslySetInnerHTML={{ __html: taxInfo }}></div>
        <div className={style3} dangerouslySetInnerHTML={{ __html: AmountInfo }}></div>
      </div>
    </>
  );
};

type OrderPrintProps = {
  specification?: number;
  data?: string;
  productName?: string;
  utncode?: string;
  skucode?: string;
};

/* This reusable control dynamically generates a checkbox in the item list, 
   adding the selected key ID to the selected items and enabling the delete button when checked. */
const OrderPrint: React.FC<OrderPrintProps> = ({ 
  specification = 2,
  data ='', 
  productName ='',
  utncode ='',
  skucode ='',
}) => {
  const utnSku = skucode ? ` - SKU: <span class='copy'>${skucode}</span>`: utncode ? ` - UTN: <span class='copy'>${utncode}</span>`:'';
  const productInfo = `${productName ? `<div class='format-product'>${productName} ${specification == Number(Code.Option.Control.FRAME) ?  utnSku : ''}</div>`: ''} ${data}`;
  const style1 = 'fl form-col100 flarea pa2 ma bright';
    return (
    <>
      <div className="form-item">
        <div className={style1} dangerouslySetInnerHTML={{ __html: productInfo }} ></div>
      </div>
    </>
  );
};


interface PanelLeftMenuProps {
  children: React.ReactNode;
  title: string;
  activeMenu: number | null;
  thisMenu: number;
  subForms?: number[];
  toggleSubMenu: (menuId: number) => void;
}

const PanelLeftMenu: React.FC<PanelLeftMenuProps> = ({ children, title, activeMenu, thisMenu, subForms, toggleSubMenu }) => {
  let hasPrivilege = '';
  if(subForms && subForms.length>0) {
    hasPrivilege = 'hide';
    subForms.forEach((form) => {
      const Privilege = Hooks.Privilege.List(form);
      if (Privilege.ALL || Privilege.VIEW) {
        hasPrivilege = '';
      }
    });
  }
  
  return (
    <div className={`mxmenu ${hasPrivilege}`} id={`mblock${thisMenu}`}>
      <p className={`icon-${title.toLowerCase()} ${activeMenu === thisMenu ? 'active' : null}`} onClick={() => toggleSubMenu(thisMenu)}>
        {title}
      </p>
      {activeMenu === thisMenu && (
        <div>
          {children}
        </div>
      )}
    </div>
  );
};

interface PrintContextMenuWrapperProps {
  setMenuPosition: (position: { x: number; y: number }) => void;
  setShowMenu: (show: boolean) => void;
  children: React.ReactNode;
}


const PrintFrame: React.FC<PrintContextMenuWrapperProps> = ({ 
  setShowMenu, 
  setMenuPosition, 
  children 
}) => {

  const handleContextMenu = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setShowMenu(true);
  };

  return (
    <div className="print-frame" onContextMenu={handleContextMenu}>
      {children}
    </div>
  );
};

// interface FormContextMenuWrapperProps {
//   setMenuPosition: (position: { x: number; y: number }) => void;
//   setShowMenu: (show: boolean) => void;
//   children: React.ReactNode;
// }

// const PrintFrame: React.FC<ChildrenProps> = ({ children }) => {
//   return (
//     <div className="print-frame">
//       {children}
//     </div>
//   );
// };

interface FormBarcodeProps {
  value: string;
  width?: number;
  height?: number;
}

const FormBarcode: React.FC<FormBarcodeProps> = ({ value, width = 1, height = 24 }) => {
  return (
    <div className='fbarcode'>
      <Barcode value={value} displayValue={false} background="transparent" width={width} height={height} margin={0} />
    </div>
  );
};

interface HtmlPrescriptionProps {
  prescriptionData?: PrescriptionData | null;
  classname?: string;
}

const HtmlPrescription: React.FC<HtmlPrescriptionProps> = ({ 
  prescriptionData,
  classname = ''
}) => {
  
  return (
    <>
      <div className={`${classname ? ` ${classname}` : ''}`}>
        {prescriptionData?.data1 && <div className='ptremarks' dangerouslySetInnerHTML={{ __html: prescriptionData?.data1 ? `<strong>Lens:</strong> ${prescriptionData?.data1}` : '', }} />}
        {prescriptionData?.data2 && <div className='ptremarks' dangerouslySetInnerHTML={{ __html: prescriptionData?.data2 ? `<strong>Matte/Tinted:</strong> ${prescriptionData?.data1}` : '', }} />}
        <table className={`prescription`}>
            <tbody>
              <tr>
                <td className='ptcol1 pthead btop bleft' rowSpan={9}>℞</td>
                <td className='ptcol2 ptfill btop'>&nbsp;</td>
                <td className='ptcol3 pttitle'>Sphere</td>
                <td className='ptcol4 pttitle'>Cylinder</td>
                <td className='ptcol5 pttitle'>Axis</td>
                <td className='ptcol6 pttitle'>Add</td>
              </tr>
              <tr>
                  <td className='ptsub'>OD</td>
                  <td className='ptvalue'>{prescriptionData?.ODSphere || ''}</td>
                  <td className='ptvalue'>{prescriptionData?.ODCylinder}</td>
                  <td className='ptvalue'>{prescriptionData?.ODAxis}</td>
                  <td className='ptvalue'>{prescriptionData?.ODAdd}</td>
              </tr>
              <tr>
                  <td className='ptsub'>OS</td>
                  <td className='ptvalue'>{prescriptionData?.OSSphere}</td>
                  <td className='ptvalue'>{prescriptionData?.OSCylinder}</td>
                  <td className='ptvalue'>{prescriptionData?.OSAxis}</td>
                  <td className='ptvalue'>{prescriptionData?.OSAdd}</td>
              </tr>
              {(prescriptionData?.Title1 || prescriptionData?.Title2 || prescriptionData?.Title3 || prescriptionData?.Title4) ? (
                <>
                  <tr>
                      <td className='ptfill'>&nbsp;</td>
                      <td className='pttitle'>{prescriptionData?.Title1}</td>
                      <td className='pttitle'>{prescriptionData?.Title2}</td>
                      <td className='pttitle'>{prescriptionData?.Title3}</td>
                      <td className='pttitle'>{prescriptionData?.Title4}</td>
                  </tr>
                  <tr>
                      <td className='ptsub'>OD</td>
                      <td className='ptvalue'>{prescriptionData?.ODValue1}</td>
                      <td className='ptvalue'>{prescriptionData?.ODValue2}</td>
                      <td className='ptvalue'>{prescriptionData?.ODValue3}</td>
                      <td className='ptvalue'>{prescriptionData?.ODValue4}</td>
                  </tr>
                  <tr>
                      <td className='ptsub'>OS</td>
                      <td className='ptvalue'>{prescriptionData?.OSValue1}</td>
                      <td className='ptvalue'>{prescriptionData?.OSValue2}</td>
                      <td className='ptvalue'>{prescriptionData?.OSValue3}</td>
                      <td className='ptvalue'>{prescriptionData?.OSValue4}</td>
                  </tr>
                </>
              ) : null}
              {(prescriptionData?.Title5 || prescriptionData?.Title6 || prescriptionData?.Title7 || prescriptionData?.Title8) ? (
                <>
                  <tr>
                    <td className='ptfill'>&nbsp;</td>
                    <td className='pttitle'>{prescriptionData?.Title5}</td>
                    <td className='pttitle'>{prescriptionData?.Title6}</td>
                    <td className='pttitle'>{prescriptionData?.Title7}</td>
                    <td className='pttitle'>{prescriptionData?.Title8}</td>
                  </tr>
                  <tr>
                    <td className='ptsub'>OD</td>
                    <td className='ptvalue'>{prescriptionData?.ODValue5}</td>
                    <td className='ptvalue'>{prescriptionData?.ODValue6}</td>
                    <td className='ptvalue'>{prescriptionData?.ODValue7}</td>
                    <td className='ptvalue'>{prescriptionData?.ODValue8}</td>
                  </tr>
                  <tr>
                    <td className='ptsub'>OS</td>
                    <td className='ptvalue'>{prescriptionData?.OSValue5}</td>
                    <td className='ptvalue'>{prescriptionData?.OSValue6}</td>
                    <td className='ptvalue'>{prescriptionData?.OSValue7}</td>
                    <td className='ptvalue'>{prescriptionData?.OSValue8}</td>
                  </tr>
                </>
              ) : null}
              {(prescriptionData?.Title9 || prescriptionData?.Title10 || prescriptionData?.Title11 || prescriptionData?.Title12) ? (
                <>
                  <tr>
                    <td className='ptfill'>&nbsp;</td>
                    <td className='pttitle'>{prescriptionData?.Title9}</td>
                    <td className='pttitle'>{prescriptionData?.Title10}</td>
                    <td className='pttitle'>{prescriptionData?.Title11}</td>
                    <td className='pttitle'>{prescriptionData?.Title12}</td>
                  </tr>
                  <tr>
                    <td className='ptsub'>OD</td>
                    <td className='ptvalue'>{prescriptionData?.ODValue9}</td>
                    <td className='ptvalue'>{prescriptionData?.ODValue10}</td>
                    <td className='ptvalue'>{prescriptionData?.ODValue11}</td>
                    <td className='ptvalue'>{prescriptionData?.ODValue12}</td>
                  </tr>
                  <tr>
                    <td className='ptsub'>OS</td>
                    <td className='ptvalue'>{prescriptionData?.OSValue9}</td>
                    <td className='ptvalue'>{prescriptionData?.OSValue10}</td>
                    <td className='ptvalue'>{prescriptionData?.OSValue11}</td>
                    <td className='ptvalue'>{prescriptionData?.OSValue12}</td>
                  </tr>
                </>
              ) : null}
              
        </tbody></table>
        <div className='ptremarks' dangerouslySetInnerHTML={{ __html: prescriptionData?.RXNotes ? `<strong>Rx Notes:</strong> ${prescriptionData?.RXNotes}` : '', }} />
      </div>
    </>
  );
};

interface HtmlPrescriptionCLProps {
  prescriptionCL?: PrescriptionData | null;
  classname?: string;
}

const HtmlPrescriptionCL: React.FC<HtmlPrescriptionCLProps> = ({ 
  prescriptionCL,
  classname = '',
}) => {
  
  return (
    <>
      <div className={`${classname ? ` ${classname}` : ''}`}>
        {prescriptionCL?.data1 && <div className='ptremarks' dangerouslySetInnerHTML={{ __html: prescriptionCL?.data1 ? `<strong>CL Fitting Status:</strong> ${prescriptionCL?.data1}` : '', }} />}
        <table className={`prescription`}>
          <tbody>
            <tr>
              <td className='clcol1 pthead btop bleft' rowSpan={9}>℞</td>
              <td className='clcol2 ptfill btop'>&nbsp;</td>
              <td className='clcol3 pttitle'>BC</td>
              <td className='clcol4 pttitle'>Dia</td>
              <td className='clcol5 pttitle'>Sphere</td>
              <td className='clcol6 pttitle'>Cylinder</td>
              <td className='clcol7 pttitle'>Axis</td>
              <td className='clcol8 pttitle'>Add</td>
            </tr>
            <tr>
                <td className='ptsub'>OD</td>
                <td className='ptvalue'>{prescriptionCL?.ODBc}</td>
                <td className='ptvalue'>{prescriptionCL?.ODDia}</td>
                <td className='ptvalue'>{prescriptionCL?.ODSphere}</td>
                <td className='ptvalue'>{prescriptionCL?.ODCylinder}</td>
                <td className='ptvalue'>{prescriptionCL?.ODAxis}</td>
                <td className='ptvalue'>{prescriptionCL?.ODAdd}</td>
            </tr>
            <tr>
                <td className='ptsub'>OS</td>
                <td className='ptvalue'>{prescriptionCL?.OSBc}</td>
                <td className='ptvalue'>{prescriptionCL?.OSDia}</td>
                <td className='ptvalue'>{prescriptionCL?.OSSphere}</td>
                <td className='ptvalue'>{prescriptionCL?.OSCylinder}</td>
                <td className='ptvalue'>{prescriptionCL?.OSAxis}</td>
                <td className='ptvalue'>{prescriptionCL?.OSAdd}</td>
            </tr>
          </tbody>
        </table>

        <table className={`prescription${classname ? ` ${classname}` : ''}`}>
          <tbody>
            <tr>
              <td className='clcol10 ptfill btop bleft'>&nbsp;</td>
              <td className='clcol11 pttitle'>OD</td>
              <td className='clcol12 pttitle'>OS</td>
            </tr>
            <tr>
                <td className='ptsub bleft'>CL Supplier</td>
                <td className='ptvalue'>{prescriptionCL?.ODSupplier}</td>
                <td className='ptvalue'>{prescriptionCL?.OSSupplier}</td>
            </tr>
            <tr>
                <td className='ptsub bleft'>CL Type</td>
                <td className='ptvalue'>{prescriptionCL?.ODType}</td>
                <td className='ptvalue'>{prescriptionCL?.OSType}</td>
            </tr>
            <tr>
                <td className='ptsub bleft'>CL Name</td>
                <td className='ptvalue'>{prescriptionCL?.ODName}</td>
                <td className='ptvalue'>{prescriptionCL?.OSName}</td>
            </tr>
            <tr>
                <td className='ptsub bleft'>CL Colour</td>
                <td className='ptvalue'>{prescriptionCL?.ODColor}</td>
                <td className='ptvalue'>{prescriptionCL?.ODColor}</td>
            </tr>
          </tbody>
        </table>

        <div className='ptremarks' dangerouslySetInnerHTML={{ __html: prescriptionCL?.RXNotes ? `<strong>Rx Notes:</strong> ${prescriptionCL?.RXNotes}` : '', }} />
      </div>
    </>
  );
};

interface HtmlMedicationProps {
  medicationData?: MedicationData | null;
  classname?: string;
}

const HtmlMedication: React.FC<HtmlMedicationProps> = ({ 
  medicationData,
  classname = '',
}) => {
  
  //const [serialNumber, setSerialNumber] = useState<number>(0);
  let serialNumber = 1;

  return (
    <>
      <div className={`${classname ? ` ${classname}` : ''}`}>
        <table className={`prescription`}>
          <tbody>
            <tr>
              <td className='clcol20 pttitle'>#</td>
              <td className='clcol21 pttitle'>Medicine</td>
              <td className='clcol22 pttitle'>Frequency</td>
              <td className='clcol23 pttitle'>Refills</td>
              <td className='clcol24 pttitle'>Remarks</td>
            </tr>
            {(medicationData &&  medicationData.Check1 !== '1' && (medicationData.Medication1 || medicationData.Frequency1 || medicationData.Refills1 || medicationData.Remarks1)) ? (
              <tr>
                <td className='clcol20 ptvalue'>{serialNumber++}</td>
                <td className='clcol21 ptvalue'>{medicationData.Medication1}</td>
                <td className='clcol22 ptvalue'>{medicationData.Frequency1}</td>
                <td className='clcol23 ptvalue'>{medicationData.Refills1}</td>
                <td className='clcol24 ptvalue'>{medicationData.Remarks1}</td>
              </tr>
            ) : null}
            {(medicationData &&  medicationData.Check2 !== '1' && (medicationData.Medication2 || medicationData.Frequency2 || medicationData.Refills2 || medicationData.Remarks2)) ? (
              <tr>
                <td className='clcol20 ptvalue'>{serialNumber++}</td>
                <td className='clcol21 ptvalue'>{medicationData.Medication2}</td>
                <td className='clcol22 ptvalue'>{medicationData.Frequency2}</td>
                <td className='clcol23 ptvalue'>{medicationData.Refills2}</td>
                <td className='clcol24 ptvalue'>{medicationData.Remarks2}</td>
              </tr>
            ) : null}
            {(medicationData &&  medicationData.Check3 !== '1' && (medicationData.Medication3 || medicationData.Frequency3 || medicationData.Refills3 || medicationData.Remarks3)) ? (
              <tr>
                <td className='clcol20 ptvalue'>{serialNumber++}</td>
                <td className='clcol21 ptvalue'>{medicationData.Medication3}</td>
                <td className='clcol22 ptvalue'>{medicationData.Frequency3}</td>
                <td className='clcol23 ptvalue'>{medicationData.Refills3}</td>
                <td className='clcol24 ptvalue'>{medicationData.Remarks3}</td>
              </tr>
            ) : null}
            {(medicationData &&  medicationData.Check4 !== '1' && (medicationData.Medication4 || medicationData.Frequency4 || medicationData.Refills4 || medicationData.Remarks4)) ? (
              <tr>
                <td className='clcol20 ptvalue'>{serialNumber++}</td>
                <td className='clcol21 ptvalue'>{medicationData.Medication4}</td>
                <td className='clcol22 ptvalue'>{medicationData.Frequency4}</td>
                <td className='clcol23 ptvalue'>{medicationData.Refills4}</td>
                <td className='clcol24 ptvalue'>{medicationData.Remarks4}</td>
              </tr>
            ) : null}
            {(medicationData &&  medicationData.Check5 !== '1' && (medicationData.Medication5 || medicationData.Frequency5 || medicationData.Refills5 || medicationData.Remarks5)) ? (
              <tr>
                <td className='clcol20 ptvalue'>{serialNumber++}</td>
                <td className='clcol21 ptvalue'>{medicationData.Medication5}</td>
                <td className='clcol22 ptvalue'>{medicationData.Frequency5}</td>
                <td className='clcol23 ptvalue'>{medicationData.Refills5}</td>
                <td className='clcol24 ptvalue'>{medicationData.Remarks5}</td>
              </tr>
            ) : null}
            {(medicationData &&  medicationData.Check6 !== '1' && (medicationData.Medication6 || medicationData.Frequency6 || medicationData.Refills6 || medicationData.Remarks6)) ? (
              <tr>
                <td className='clcol20 ptvalue'>{serialNumber++}</td>
                <td className='clcol21 ptvalue'>{medicationData.Medication6}</td>
                <td className='clcol22 ptvalue'>{medicationData.Frequency6}</td>
                <td className='clcol23 ptvalue'>{medicationData.Refills6}</td>
                <td className='clcol24 ptvalue'>{medicationData.Remarks6}</td>
              </tr>
            ) : null}
            {(medicationData &&  medicationData.Check7 !== '1' && (medicationData.Medication7 || medicationData.Frequency7 || medicationData.Refills7 || medicationData.Remarks7)) ? (
              <tr>
                <td className='clcol20 ptvalue'>7</td>
                <td className='clcol21 ptvalue'>{medicationData.Medication7}</td>
                <td className='clcol22 ptvalue'>{medicationData.Frequency7}</td>
                <td className='clcol23 ptvalue'>{medicationData.Refills7}</td>
                <td className='clcol24 ptvalue'>{medicationData.Remarks7}</td>
              </tr>
            ) : null}
            {(medicationData &&  medicationData.Check8 !== '1' && (medicationData.Medication8 || medicationData.Frequency8 || medicationData.Refills8 || medicationData.Remarks8)) ? (
              <tr>
                <td className='clcol20 ptvalue'>8</td>
                <td className='clcol21 ptvalue'>{medicationData.Medication8}</td>
                <td className='clcol22 ptvalue'>{medicationData.Frequency8}</td>
                <td className='clcol23 ptvalue'>{medicationData.Refills8}</td>
                <td className='clcol24 ptvalue'>{medicationData.Remarks8}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className='ptremarks' dangerouslySetInnerHTML={{ __html: medicationData?.Notes ? `<strong>Notes:</strong> ${medicationData?.Notes}` : '', }} />
      </div>
    </>
  );
};

export const Control = {
    SelectAll: SelectAll,
    SearchBar: SearchBar,
    Pagination: Pagination,
    RecordInfo: RecordInfo,
    Dropdown: Dropdown,
    DashList: DashList,
    DropdownInline: DropdownInline,
    DropdownFilter: DropdownFilter,
    ListContextMenu: ListContextMenu,
    FormContextMenu: FormContextMenu,
    PrintContextMenu: PrintContextMenu,
    CalendarContextMenu: CalendarContextMenu,
    ReportContextMenu: ReportContextMenu,
    FormPopup: FormPopup,
    FormPopup2: FormPopup2,
    Separator: FormSeparator,
    Box: FormBox,
    Row: FormRow,
    Flex: FormFlex,
    Resize: FormResize,
    Column: FormColumn,
    ButtonColumn: ButtonColumn,
    Container: Container,
    List:{
      Frame: ListFrame,
      Panel: ListPanel,
      Tools: ListTools,
      DashTool: DashTools,
      Data: ListData,
      DashData: DashData,
      Box: ListBox,
      Title: ListTitle,
      Filter: ListFilter,
      Info: ListInfo,
    },
    Form:{
      Frame: FormFrame,
      CalendarFrame: CalendarFrame,
      CalendarForm: CalendarForm,
      Data: FormData,
      Table: FormTable,
      Columns: FormColumns,
      Title: FormTitle,
      Footer: FormFooter,
      List: FormList,
      Index: FormIndex,
      BoxPanel: BoxPanel,
      Item: FormItem,
      Edit: FormEdit,
      Sale: FormSale,
      Print: FormPrint,
      Text: FormText,
      Date: FormDate,
      Number: FormNumber,
      Amount: FormAmount,
      Option: FormOption,
      Check: FormCheck,
      TextArea: FormTextArea,
      OrderTextArea: OrderTextArea,
      OrderPrint: OrderPrint,
      Barcode: FormBarcode,
    },
    Print:{
      Frame: PrintFrame,
      Data: PrintData,
      Landscape: Printlandscape,
      Table: PrintTable,
      Title: PrintTitle,
      List: PrintList,
      Index: PrintIndex,
      Item: PrintItem,
      Date: PrintDate,
      Text: PrintText,
      Number: PrintNumber,
      Product: PrintProduct,
      Term: PrintTerm,
    },
    Report:{
      Frame: ReportFrame,
      Panel: ReportPanel,
      Tools: ReportTools,
      Left: ReportLeft,
      Right: ReportRight,
      Info: ReportInfo,
      Rows: ReportRows,
    },
    Html:{
      Prescription: HtmlPrescription,
      PrescriptionCL: HtmlPrescriptionCL,
      Medication: HtmlMedication,
    },
    Grid:{
      Row: ListItem,
      Data: ContextMenuWrapper,
      Items: MultiGrid,
      Modify: ItemModify,
      Double: ItemDouble,
      Sale: ItemSale,
      Print: ItemPrint,
      Preview: ItemPreview,
      HashLink: HashLink,
      HashTarget: HashTarget,
      Link: ItemLink,
      SaleLink: SaleLink,
      Text: ItemText,
      Date: ItemDate,
      Number: ItemNumber,
      Check: CheckboxControl,
      Actions: ListActions,
    },
    Panel:{
      LeftMenu: PanelLeftMenu,
    },
    Tool:{
      Left: ToolLeft,
      Right: ToolRight,
    },
    Tab:{
      Header: TabHeader,
      HeaderSuper: TabHeaderSuper,
      Frame: TabFrame,
      Title: TabTitle,
      Content: TabContent,
    },
    Action:{
      All: ActionAll,
      Link: ActionLink,
      Edit: ActionEdit,
      History: ActionHistory,
      Delete: ActionDelete,
    }
  }
